/**
 * UI adapter: bridges React save/load flows to SaveGame / LoadGame use cases (UC-02, UC-03)
 * Enable via architectureFeatures.useRepositoryPersistence
 */
import { useCallback } from 'react';
import type { SaveGameInput } from '../../../application/use-cases/SaveGameUseCase';
import type { GameSaveSnapshot } from '../../../domain/ports/IGameRepository';
import {
  saveGameUseCase,
  loadGameUseCase,
} from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';

/** Persist game state through the repository use case */
export async function saveGame(input: SaveGameInput): Promise<void> {
  if (!architectureFeatures.useRepositoryPersistence) {
    return;
  }
  await saveGameUseCase.execute(input);
}

/** Load persisted game state; returns null when flag is off or no save exists */
export async function loadGame(): Promise<GameSaveSnapshot | null> {
  if (!architectureFeatures.useRepositoryPersistence) {
    return null;
  }
  return loadGameUseCase.execute();
}

/** Whether a save exists in the configured repository */
export function hasSave(): boolean {
  if (!architectureFeatures.useRepositoryPersistence) {
    return false;
  }
  return loadGameUseCase.hasSave();
}

/**
 * Hook helper for future App.tsx integration.
 * Returns stable callbacks that run UC-02/UC-03 when the feature flag is on.
 */
export function useGamePersistence() {
  const save = useCallback(async (input: SaveGameInput) => {
    await saveGame(input);
  }, []);

  const load = useCallback(async () => loadGame(), []);

  const checkHasSave = useCallback(() => hasSave(), []);

  return { saveGame: save, loadGame: load, hasSave: checkHasSave };
}